import * as React from "react";
import { useState } from "react";
import { GridDataStateChangeEvent, GridRowProps, GridToolbar } from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import { toast } from "react-toastify";
import { DatePickerPropsContext } from "@progress/kendo-react-dateinputs";
import { NumericTextBoxPropsContext } from "@progress/kendo-react-inputs";
import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { useTheme } from "@mui/material";
import { GridDataLoader } from "../../../Grid/GridDataLoader";
import StyledKendoGrid from "../../../Grid/StyledKendoGrid";
import PdsrValidationItemGridDataStateKey from "./PdsrValidationItemGridDataStateKey";
import PdsrValidationItemGridColumns from "./PdsrValidationItemGridColumns";
import {
  DATA_ITEM_KEY,
  EDIT_FIELD,
  gridPagerDefaultSettings,
  idGetter,
  SELECTED_FIELD,
} from "../../../Grid/GridConstants";
import CreateButton from "./Controls/CreateButton";
import CopyButton from "./Controls/CopyValidationItemsButton";
import ApproveItemsButton from "./Controls/ApproveItemsButton";
import CancelCreateButton from "./Controls/CancelCreateButton";
import { GridSelectableRowsProps } from "../../../Grid/GridSelectableRowsProps";
import InlineEditProps from "../../../Grid/InlineEdit/InlineEditProps";
import createPdsrValidationItem from "./CreatePdsrValidationItem";
import { InlineEditRowRender } from "../../../Grid/InlineEdit/InlineEditRowRender";
import { InitialState } from "../../../Grid/InitialDataState";
import updatePdsrValidationItem from "./UpdatePdsrValidationItem";
import ExcelExportButton from "../../../Grid/ExcelExport/ExcelExportButton";
import DeleteItemsButton from "../../../Grid/DeleteItemsButton/DeleteItemsButton";
import { ValidationItemProps } from "./ValidationItemProps";
import useSavedState from "../../../Grid/UseSavedState";
import { OnRowClickType } from "../../../Layout/Grid/GridRowClickProps";
import { combinedUrls } from "../../../URL/functions";
import { validationBacklogUrls } from "../../../URL/ApplicationUrls";
import { PdsrValidationItem } from "./PdsrValidationItem";
import PageHeader from "../../../Layout/Header/PageHeader";
import { useDefaultDatePickerProps } from "../../../Grid/useDefaultDatePickerProps";
import { useDefaultNumericTextBoxProps } from "../../../Grid/useDefaultNumericTextBoxProps";
import StatePersistenceButtons from "../../../Grid/StatePersistenceButtons";
import { isItemValidWithoutToast, itemAlreadyExists } from "./ValidateItems";
import RenderAuthorized from "../../../Auth/RenderAuthorized";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";
import { PdsrValidationItemMapper } from "./PdsrValidationItemMapper";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";

export enum PdsrValidationItemEditMode {
  Update = 0,
  Create = 1,
  Duplicate = 2,
}

export default function PdsrValidationItemGrid() {
  const baseUrl = validationBacklogUrls.pdsrs.apiUrl;

  const [dataResult, setDataResult] = React.useState<DataResult>({
    data: [],
    total: 0,
  });
  const [dataState, setDataState] = useSavedState<State>(
    PdsrValidationItemGridDataStateKey,
    InitialState
  );

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const onDataReceived = (newDataResult: DataResult) => {
    setDataResult(newDataResult);
  };

  const [selectedState, setSelectedState] = React.useState<{
    [id: number]: boolean | number[];
  }>({});
  const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
  const selectableRowsProps = GridSelectableRowsProps<PdsrValidationItem>({
    selectedState,
    setSelectedState,
    setSelectedIds,
    dataItemKey: DATA_ITEM_KEY,
  });
  const deselectAll = () => {
    setSelectedIds([]);
    setSelectedState({});
  };

  const onRowClick: OnRowClickType = (dataItem: PdsrValidationItem) => {
    const id = dataItem[DATA_ITEM_KEY];
    if (!selectedState[id]) {
      setSelectedState({ [id]: true });
      setSelectedIds([...selectedIds, id]);
    }
  };

  const [editState, setEditState] = React.useState<number>();

  const isCreatingItemsInline = dataResult.data.filter((x) => !x.Id).length > 0;
  const [editMode, setEditMode] = useState(PdsrValidationItemEditMode.Update);
  const inlineEditProps = InlineEditProps({
    idField: DATA_ITEM_KEY,
    dataResult,
    onDataReceived,
    setEditState: (rowNum) => {
      if (isCreatingItemsInline) {
        return;
      }

      setEditMode(PdsrValidationItemEditMode.Update);
      setEditState(rowNum);
    },
    saveChanges: async (changedItem) => {
      if (await itemAlreadyExists(changedItem)) {
        throw new Error("Item already exists");
      }
      return updatePdsrValidationItem(combinedUrls(baseUrl, idGetter(changedItem)), changedItem);
    },
    isReadonly: false,
  });

  const addToDataResult = (listOfItems: Array<PdsrValidationItem>) => {
    const copyData = [...dataResult.data, ...listOfItems];
    setDataResult({
      data: copyData,
      total: dataResult.total + listOfItems.length,
    });
  };

  async function addRecord(updatedItem: ValidationItemProps) {
    try {
      const item = await createPdsrValidationItem(updatedItem);

      // replace item without id with new item
      const itemsWithIds = dataResult.data.filter((x) => x.Id);
      const copyData = [...itemsWithIds, item];
      setDataResult({
        data: copyData,
        total: copyData.length,
      });

      toast("Validation item was successfully added", {
        type: "success",
      });
    } catch (error: any) {
      toast("Error while adding new validation item", {
        type: "error",
      });
    }
  }

  const exitEdit = async (dataItem: PdsrValidationItem) => {
    const id = dataItem.Id;
    if (!id) {
      const isValid = await isItemValidWithoutToast(dataItem);
      if (!isValid) {
        return;
      }
      if (await itemAlreadyExists(dataItem)) {
        return;
      }
      await addRecord(dataItem);
      deselectAll();
    }

    setEditState(undefined);
  };

  const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    return (
      <InlineEditRowRender
        originalProps={props}
        tr={trElement}
        editField={EDIT_FIELD}
        exitEdit={exitEdit}
      />
    );
  };

  const gridUrl = combinedUrls(baseUrl, "/KendoGrid");
  const datePickerProps = useDefaultDatePickerProps();
  const numericTextBoxProps = useDefaultNumericTextBoxProps();
  const GridRef = React.useRef<any>();
  const theme = useTheme();

  const allButCheckboxColumn = GridRef?.current?.columns.slice(1);

  const isUserAdminOrRiskManager = useIsUserInRoles(
    AuthorizationPolicies.RequireAdminRiskmanagerRoles
  );
  return (
    <div>
      <PageHeader headerText="PDSR Validation Items">
        <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
          {/* Todo: Button auf Wunsch von Kunden deaktiviert. Stand: 10.12.2024 */}
          <DeleteItemsButton
            url={baseUrl}
            selectedIds={selectedIds}
            dataResult={dataResult}
            setDataResult={setDataResult}
            afterDeleteItems={() => deselectAll}
            renderStyle="pageHeaderButton"
            disabled
          />
          {/* Todo: Button auf Wunsch von Kunden deaktiviert. Stand: 10.12.2024 */}
          <ApproveItemsButton
            dataResult={dataResult}
            setDataResult={setDataResult}
            selectedIdsList={selectedIds}
            deselectAll={deselectAll}
            disabled
          />
        </RenderAuthorized>
      </PageHeader>
      <DatePickerPropsContext.Provider value={datePickerProps}>
        <NumericTextBoxPropsContext.Provider value={numericTextBoxProps}>
          <StyledKendoGrid
            muiTheme={theme}
            ref={GridRef}
            height="70vh"
            data={{
              data: dataResult.data.map((item) => ({
                ...item,
                [EDIT_FIELD]: item[DATA_ITEM_KEY] === editState,
                [SELECTED_FIELD]: selectedState[idGetter(item)],
              })),
              total: dataResult.total,
            }}
            sortable
            filterable
            pageable={{
              ...gridPagerDefaultSettings,
            }}
            resizable
            scrollable="scrollable"
            {...dataState}
            onDataStateChange={onDataStateChange}
            {...inlineEditProps}
            {...selectableRowsProps}
            rowRender={rowRender}
          >
            <GridToolbar>
              {!isCreatingItemsInline ? (
                <>
                  <StatePersistenceButtons
                    localStorageKey={PdsrValidationItemGridDataStateKey}
                    initialState={InitialState}
                    dataState={dataState}
                    resetButtonText="RESET FILTER"
                    saveButtonText="SAVE FILTER"
                    align="left"
                  />
                  <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
                    {/* Create Button auf Kundenwunsch disabled um Pdsr testweise nur über Upload Funktion zu erlauben */}
                    <CreateButton
                      onClick={(listOfItems: Array<PdsrValidationItem>) => {
                        setEditMode(PdsrValidationItemEditMode.Create);
                        addToDataResult(listOfItems);
                      }}
                      disabled
                    />
                    <CopyButton
                      selectedIds={selectedIds}
                      dataResult={dataResult}
                      addValidationItemsToData={(listOfItems: Array<PdsrValidationItem>) => {
                        setEditMode(PdsrValidationItemEditMode.Duplicate);
                        addToDataResult(listOfItems);
                      }}
                    />
                  </RenderAuthorized>
                  <ExcelExportButton
                    url={gridUrl}
                    dataState={dataState}
                    columns={allButCheckboxColumn}
                    fileName="PDSR_Validation_Items_Export"
                    buttonText="EXPORT LIST"
                    exportMapping={(pdsrValidationItem: PdsrValidationItem) =>
                      PdsrValidationItemMapper.toExportPdsrValidationItem(pdsrValidationItem)
                    }
                  />
                </>
              ) : (
                <CancelCreateButton
                  dataResult={dataResult}
                  setDataResult={setDataResult}
                  setEditMode={setEditMode}
                />
              )}
            </GridToolbar>
            {[
              <Column
                key="SelectHeader"
                field={SELECTED_FIELD}
                filterable={false}
                width="50px"
                headerSelectionValue={
                  dataResult.data.findIndex((item) => !selectedState[idGetter(item)]) === -1
                }
              />,
              ...PdsrValidationItemGridColumns({
                editMode,
                onRowClick,
                isReadOnly: !isUserAdminOrRiskManager,
              }),
            ]}
          </StyledKendoGrid>
        </NumericTextBoxPropsContext.Provider>
      </DatePickerPropsContext.Provider>
      <GridDataLoader url={gridUrl} dataState={dataState} onDataReceived={onDataReceived} />
    </div>
  );
}
